import PropTypes from "prop-types";
import React from "react";

import { fireTrack } from "@swa-ui/analytics";
import { SMALL, useDeviceInfo } from "@swa-ui/browser";
import { Accordion } from "@swa-ui/core";
import i18n from "@swa-ui/locale";

import { CallToAction } from "../CallToAction";
import { HtmlValue } from "../HtmlValue";
import { PlacementHeading } from "../PlacementHeading";
import { usePlacement } from "../usePlacement";
import { getMissingTemplateKeys } from "../usePlacement/getMissingTemplateKeys";
import interpolate from "../usePlacement/interpolate";
import styles from "./AccordionBlock.module.scss";

/**
 * AccordionBlock renders a list of one or more Accordion components, with a main header and CTA Link.
 */

export const AccordionBlock = (props) => {
  const {
    placement,
    placement: { callToAction, heading, items, layoutType } = {},
    placementData,
  } = props;
  const { screenSize } = useDeviceInfo();
  const { setPlacementsAnalytics } = usePlacement();
  const missingTemplateKeys = getMissingTemplateKeys(placement, placementData);
  const placementDataFormatted = { ...placementData };

  if (missingTemplateKeys.length) {
    missingTemplateKeys.forEach((key) => (placementDataFormatted[key] = ""));
  }

  return renderAccordionSection();

  function renderAccordionSection() {
    let content;

    if (layoutType === "compact") {
      content = screenSize === SMALL ? renderCompactSmallAccordion() : renderCompactAccordion();
    } else {
      content = renderDefaultAccordion();
    }

    return content;
  }

  function renderCompactSmallAccordion() {
    return (
      <>
        {renderHeading()}
        {renderAccordion()}
        {renderCallToAction()}
      </>
    );
  }

  function renderCompactAccordion() {
    return (
      <>
        <div className={styles.headingCtaContainer}>
          {renderHeading()}
          {renderCallToAction()}
        </div>
        {renderAccordion()}
      </>
    );
  }

  function renderDefaultAccordion() {
    return (
      <>
        <div className={styles.defaultHeading}>{renderHeading()}</div>
        {renderAccordion()}
        <div className={styles.callToAction}>{renderCallToAction()}</div>
      </>
    );
  }

  function renderHeading() {
    let headingValue;

    if (typeof heading === "string") {
      headingValue = i18n(interpolate(heading, placementDataFormatted));
    } else if (heading?.value) {
      headingValue = i18n(interpolate(heading.value, placementDataFormatted));
    }

    return (
      <PlacementHeading
        value={headingValue}
        {...(heading?.value ? { headingProps: heading.headingProps } : {})}
      />
    );
  }

  function renderCallToAction() {
    return callToAction && <CallToAction {...callToAction} />;
  }

  function renderAccordion() {
    return (
      items.length &&
      items.map((accordion) => {
        const { label } = accordion;
        const key = label?.value || label;

        return (
          <Accordion key={key} {...getAccordionProps(accordion)}>
            <HtmlValue htmlValue={i18n(interpolate(accordion.children, placementDataFormatted))} />
          </Accordion>
        );
      })
    );
  }

  function renderLabel(label) {
    let Node;

    if (label?.value) {
      Node = (
        <span style={{ ...label }}>{i18n(interpolate(label.value, placementDataFormatted))}</span>
      );
    } else {
      Node = i18n(interpolate(label, placementDataFormatted));
    }

    return Node;
  }

  function getAccordionProps(accordion) {
    const { id, label, revealed } = accordion;

    return {
      className: styles.accordion,
      id,
      label: renderLabel(label),
      name: createTrackingId(label),
      onChange: (revealState) => handleChange(revealState, label),
      revealed,
    };
  }

  function handleChange(revealState, label) {
    if (revealState) {
      const accordionLabel = label?.value || label;

      fireTrack("squid", {
        page_description: `accordion: ${heading} expanded`,
        topic: accordionLabel,
      });

      setPlacementsAnalytics({
        eventDescription: `accordion: ${heading}|${accordionLabel}`,
        eventType: "accordion",
      });
    }
  }

  function createTrackingId(label) {
    const name = label?.value || label;

    return name.replace(/\s+/g, "").substring(0, 20);
  }
};

AccordionBlock.displayName = "AccordionBlock";
AccordionBlock.propTypes = {
  /** Object that contains placement props, including templateKeys, heading, and items. */
  placement: PropTypes.shape({
    /** Object that contains link props. */
    callToAction: PropTypes.object,

    /** Heading creates a heading element for the specified heading level. */
    heading: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),

    /** Object that contains accordion props. */
    items: PropTypes.arrayOf(PropTypes.object),
  }),

  /** Object that contains string values corresponding to templateKeys and/or dynamic analytics values for the accordionBlock. */
  placementData: PropTypes.shape({
    /** The dynamic value that will be used as an event description for analytics. */
    analyticsValue: PropTypes.string,
  }),
};
